import { useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { t } from '../i18n/index.js';
import { StatsCard } from '../components/StatsCard.js';
import { CoinsCard } from '../components/CoinsCard.js';
import { SoundSettingsCard } from '../components/SoundSettingsCard.js';
import { NewPasswordFields, newPasswordError } from '../components/PasswordFields.js';
import type { MeUser } from '../../shared/protocol.js';

export function AccountView(props: { user: MeUser }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: Event) => {
    e.preventDefault();
    const pwError = newPasswordError(next, repeat, t.account.mismatch);
    if (pwError) {
      setMessage({ ok: false, text: pwError });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await api('POST', '/api/auth/password', { currentPassword: current, newPassword: next });
      setMessage({ ok: true, text: t.account.saved });
      setCurrent('');
      setNext('');
      setRepeat('');
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'network';
      setMessage({ ok: false, text: code === 'invalid_credentials' ? t.account.currentWrong : t.errors[code] });
    } finally {
      setBusy(false);
    }
  };

  // A guest (decision 2026-09-29): no password and no statistics, only the visit.
  if (props.user.guestUntil !== null) {
    const until = new Date(props.user.guestUntil).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
    return (
      <div class="stack">
        <section class="card">
          <h2>{t.guest.accountTitle}</h2>
          <dl class="meta">
            <div>
              <dt>{t.account.displayName}</dt>
              <dd>{props.user.displayName}</dd>
            </div>
          </dl>
          <p>{t.guest.accountText(until)}</p>
          <p class="hint">{t.guest.register}</p>
        </section>
        <SoundSettingsCard />
      </div>
    );
  }

  return (
    <div class="stack">
      <section class="card">
        <h2>{t.account.title}</h2>
        <dl class="meta">
          <div>
            <dt>{t.account.username}</dt>
            <dd>{props.user.username}</dd>
          </div>
          <div>
            <dt>{t.account.displayName}</dt>
            <dd>{props.user.displayName}</dd>
          </div>
          <div>
            <dt>{t.account.role}</dt>
            <dd>{t.roles[props.user.role]}</dd>
          </div>
        </dl>
      </section>
      <CoinsCard />
      <StatsCard />
      <form class="card form" onSubmit={submit}>
        <h3>{t.account.changePassword}</h3>
        {/* Hidden username helps password managers associate the new password. */}
        <input type="text" autocomplete="username" value={props.user.username} hidden readOnly />
        <label>
          {t.account.currentPassword}
          <input type="password" value={current} onInput={(e) => setCurrent(e.currentTarget.value)} autocomplete="current-password" required />
        </label>
        <NewPasswordFields
          value={next}
          repeat={repeat}
          onValue={setNext}
          onRepeat={setRepeat}
          label={t.account.newPassword}
          repeatLabel={t.account.repeatPassword}
        />
        {message && (
          <p class={message.ok ? 'success' : 'error'} role="status">
            {message.text}
          </p>
        )}
        <button class="btn btn-primary" type="submit" disabled={busy}>
          {t.account.save}
        </button>
      </form>
      <SoundSettingsCard />
    </div>
  );
}
